import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import type { Country } from '@freelancer/datastore/collections/countries';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import type { Location } from '@freelancer/datastore/collections/project-view-users';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import { _usersLocationTransformCountry } from '@freelancer/datastore/collections/users';
import { Geolocation } from '@freelancer/geolocation';
import type { GoogleMapsAutocomplete } from '@freelancer/google-maps';
import { GoogleMaps } from '@freelancer/google-maps';
import { Localization } from '@freelancer/localization';
import { TimeUtils } from '@freelancer/time-utils';
import { InputComponent, InputSize, InputType } from '@freelancer/ui/input';
import { FontColor, TextSize } from '@freelancer/ui/text';
import { isDefined, isEqual } from '@freelancer/utils';
import type { Observable, Subscription } from 'rxjs';
import { BehaviorSubject, combineLatest } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { IconColor, IconSize } from '../icon';
import type { MapCoordinates } from '../map';
import { UI_CONFIG } from '../ui.config';
import { UiConfig } from '../ui.interface';
import { dirtyAndValidate } from '../validators';
import {
  isLocationInputCustomAddress,
  isLocationInputMapLocation,
} from './location-input.helpers';
import type {
  LocationInputCustomAddress,
  LocationInputCustomCoordinate,
  LocationInputMapLocation,
} from './location-input.types';
import {
  AutocompleteLocationType,
  GoogleMapsLocationTypes,
  LocationError,
} from './location-input.types';

@Component({
  selector: 'fl-location-input',
  template: `
    <div #inputWrapper>
      <div class="InputSection">
        <fl-input
          #input
          class="LocationInput"
          [attr.aria-invalid]="nativeControl.invalid"
          [label]="label"
          [control]="nativeControl"
          [disabled]="disabled || control.disabled"
          [iconEnd]="iconEnd"
          [placeholder]="placeholder"
          [rightIconColor]="
            showRemoveLocationButton ? IconColor.FOREGROUND : IconColor.INHERIT
          "
          [rightIconSize]="
            showRemoveLocationButton ? IconSize.SMALL : IconSize.MID
          "
          [shakeOnBlur]="false"
          [size]="inputSize"
          [type]="InputType.TEXT"
          [validationStateCheck]="dynamicValidation"
          (iconEndClick)="handleIconClick()"
        ></fl-input>
      </div>

      <fl-card
        *ngIf="
          (showAutocompleteDropdown$ | flAsync) &&
          (autocompletePredictions$ | flAsync)
        "
        class="AutocompletePredictions"
        [edgeToEdge]="true"
      >
        <fl-list
          [clickable]="true"
          [selectByKeyboard]="true"
        >
          <fl-list-item
            *ngFor="
              let place of autocompletePredictions$ | flAsync;
              trackBy: trackByPlaceId
            "
            (click)="selectLocation(place)"
            (selected)="selectLocation(place)"
          >
            <p>
              {{ place.description }}
            </p>
          </fl-list-item>
        </fl-list>
      </fl-card>
    </div>

    <div
      *ngIf="error !== undefined"
      class="ErrorContainer"
    >
      <fl-icon
        class="IconError"
        [name]="'ui-warning-alt-v2'"
        [color]="IconColor.ERROR"
        [size]="IconSize.XSMALL"
        [flMarginRight]="'xxsmall'"
      ></fl-icon>
      <p
        class="text-error text-xxsmall"
        [ngSwitch]="error"
      >
        <ng-container
          *ngSwitchCase="LocationError.AUTOCOMPLETE_FAILED"
          i18n="Location input error message"
        >
          Could not fetch location data from Google. Please try entering your
          location again.
        </ng-container>
        <ng-container
          *ngSwitchCase="LocationError.AUTOCOMPLETE_ZERO_RESULTS"
          i18n="Location input error message"
        >
          Could not find this location. Please check that the location details
          are correct.
        </ng-container>
        <ng-container
          *ngSwitchCase="LocationError.DETECT_LOCATION_FAILED"
          i18n="Location input error message"
        >
          Could not detect your location. Please try again.
        </ng-container>
        <ng-container
          *ngSwitchCase="LocationError.DETECT_LOCATION_PERMISSION_DENIED"
          i18n="Location input error message"
        >
          Could not detect your location. Please check your location sharing
          permissions and try again.
        </ng-container>
        <ng-container
          *ngSwitchCase="LocationError.DETECT_LOCATION_UNSUPPORTED"
          i18n="Location input error message"
        >
          Your browser does not support location sharing. Please manually enter
          your location or try a different browser.
        </ng-container>
        <ng-container
          *ngSwitchCase="LocationError.SELECT_FROM_DROPDOWN"
          i18n="Location input error message"
        >
          Please select a location from the dropdown
        </ng-container>
      </p>
    </div>
  `,
  styleUrls: ['./location-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationInputComponent
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  IconSize = IconSize;
  IconColor = IconColor;
  InputType = InputType;
  FontColor = FontColor;
  TextSize = TextSize;
  LocationError = LocationError;

  /** Country restrictions override for autocomplete predictions. */
  @Input() autocompleteCountries?: string | string[];
  @Input() control: FormControl<
    | LocationInputCustomCoordinate
    | LocationInputCustomAddress
    | LocationInputMapLocation
    | undefined
  >;
  /** Whether or not to display the detect location button  */
  @Input() detectLocation = false;
  @Input() disabled = false;
  @Input() inputSize = InputSize.MID;
  @Input() label?: string;
  @Input() placeholder?: string = 'Enter a location';
  /** Set to false to prevent additional geocode request to retrieve postcode for partial addresses */
  @Input() postalCodeRequired = true;
  /**
   * The `type` passed to the GMaps API when doing autocompletion.
   * See https://developers.google.com/places/web-service/autocomplete#place_types
   */
  @Input() locationType = AutocompleteLocationType.ALL;
  /** Limits the results to the countries if given.  */
  @Input() countryRestrictions?: readonly Country[];
  /** Include postal code, if present, in the location full address */
  @Input() displayPostalCode = false;
  /** First validation happens only on blur, and validates on value changes once an error exists */
  @Input() dynamicValidation = false;

  @Output() clearLocation = new EventEmitter();

  @ViewChild('input') input: InputComponent<string>;
  @ViewChild('inputWrapper', { read: ElementRef })
  inputWrapper: ElementRef<HTMLElement>;

  autocomplete?: GoogleMapsAutocomplete;
  private autocompleteSubject$ = new BehaviorSubject<
    google.maps.places.AutocompletePrediction[]
  >([]);
  autocompletePredictions$: Observable<
    google.maps.places.AutocompletePrediction[]
  >;
  nativeControl = new FormControl('', { nonNullable: true });
  iconEnd?: string;
  showRemoveLocationButton = false;
  private showAutocompleteDropdownSubject$ = new BehaviorSubject(false);
  showAutocompleteDropdown$ =
    this.showAutocompleteDropdownSubject$.asObservable();

  /**
   * TODO: T267853 - Refactor component's error state.
   * Use `nativeControl` to handle all validation.
   * TICKET: T104219
   */
  error?: LocationError;

  private valueResetSubscription?: Subscription;
  private statusChangeSubscription?: Subscription;
  private placeChangedSubscription?: Subscription;
  private controlValueChangeSubscription?: Subscription;

  constructor(
    private geolocation: Geolocation,
    private googleMaps: GoogleMaps,
    private localization: Localization,
    private timeUtils: TimeUtils,
    @Inject(UI_CONFIG) public uiConfig: UiConfig,
  ) {}

  // Listening for clicks outside input component and autocomplete dropdown
  @HostListener('document:click', ['$event'])
  documentClick(event: MouseEvent): void {
    // Toggle dropdown and validate input if user clicks outside input wrapper
    if (
      event &&
      this.inputWrapper.nativeElement.contains(event.target as HTMLElement)
    ) {
      this.showAutocompleteDropdownSubject$.next(true);
    } else {
      this.showAutocompleteDropdownSubject$.next(false);
      // Validate only if visited
      if (this.nativeControl.touched) {
        this.validateInput();
        if (!this.nativeControl.value) {
          // Only used by the second input location in multiple-location-input component.
          this.clearLocation.emit();
        }
      }
    }
  }

  ngOnInit(): void {
    this.autocompletePredictions$ = this.autocompleteSubject$.asObservable();

    if (this.control && this.control.validator) {
      this.nativeControl.setValidators(this.control.validator);
      this.nativeControl.updateValueAndValidity();
    }

    this.valueResetSubscription = combineLatest([
      this.nativeControl.valueChanges,
      this.showAutocompleteDropdown$,
    ])
      .pipe(
        withLatestFrom(this.control.valueChanges.pipe(startWith(undefined))),
        filter(
          ([[nativeControlValue, showAutocompleteDropdown], controlValue]) => {
            const controlValueFullAddress =
              (isLocationInputCustomAddress(controlValue) &&
                controlValue.fullAddress) ||
              (isLocationInputMapLocation(controlValue) &&
                controlValue.location?.fullAddress);
            return (
              showAutocompleteDropdown &&
              (controlValueFullAddress
                ? controlValueFullAddress !== nativeControlValue
                : true)
            );
          },
        ),
        map(([[nativeControlValue]]) => nativeControlValue),
        distinctUntilChanged(),
        tap(nativeControlValue => {
          this.handleDynamicValidation();
          this.toggleRemoveLocationButton();
          this.setIconEnd();
        }),
        this.timeUtils.rxDebounceTime(500),
        switchMap(async nativeControlValue => {
          let autocompleteResults: google.maps.places.AutocompletePrediction[] =
            [];

          const locationTypes =
            this.locationType === AutocompleteLocationType.ALL
              ? undefined
              : [this.locationType];

          // Get country restrictions for case when autocompleteCountries is undefined
          const countryRestrictions = this.countryRestrictions
            ? this.countryRestrictions
                .filter(isDefined)
                .map(country => country.code)
            : [];

          let autocompletePredictionInput: google.maps.places.AutocompletionRequest =
            {
              input: nativeControlValue,
              componentRestrictions: {
                country: this.autocompleteCountries ?? countryRestrictions,
              },
              types: locationTypes,
            };
          if (this.uiConfig.theme === 'freightlancer') {
            autocompletePredictionInput = {
              language: this.localization.languageCode,
              ...autocompletePredictionInput,
            };
          }

          autocompleteResults = await this.googleMaps
            .getAutocompletePredictions(autocompletePredictionInput)
            .then(predictions => {
              switch (predictions.status) {
                case google.maps.places.PlacesServiceStatus.OK: {
                  if (this.nativeControl.errors) {
                    this.error = LocationError.SELECT_FROM_DROPDOWN;
                  }
                  return predictions.result;
                }
                case google.maps.places.PlacesServiceStatus.ZERO_RESULTS: {
                  this.nativeControl.setErrors({ invalid: '' });
                  this.error = LocationError.AUTOCOMPLETE_ZERO_RESULTS;
                  break;
                }
                default: {
                  this.nativeControl.setErrors({ invalid: '' });
                  this.error = LocationError.AUTOCOMPLETE_FAILED;
                  break;
                }
              }

              return [];
            });
          return autocompleteResults;
        }),
      )
      .subscribe(autocompleteResults => {
        this.autocompleteSubject$.next(autocompleteResults);
      });

    this.statusChangeSubscription = this.control.statusChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.validateInput();
      });

    // Listen to changes in the input control value, and apply the full address
    // to the native control's value.
    this.controlValueChangeSubscription = this.control.valueChanges
      .pipe(distinctUntilChanged(isEqual))
      .subscribe(formControlValue => {
        const fullAddress =
          formControlValue?.fullAddress ||
          formControlValue?.location?.fullAddress;

        if (fullAddress) {
          this.setFullAddress(fullAddress);
          this.toggleRemoveLocationButton();
          this.setIconEnd();
        } else if (!isDefined(formControlValue)) {
          this.handleLocationRemove();
        }
      });
  }

  ngAfterViewInit(): void {
    this.googleMaps.load().then(loaded => {
      if (loaded) {
        // set default value if it exists
        if (this.control.value) {
          // Control can be in different formats
          // eg. In the LocationInputMapLocation<Location> format or
          // { fullAddress, longitude, latitude } where fullAddress is derived from
          // the LocationInputMapLocation<Location> location.fullAddress
          if (isLocationInputCustomAddress(this.control.value)) {
            this.setFullAddress(this.control.value.fullAddress);
          } else if (
            isLocationInputMapLocation(this.control.value) &&
            this.control.value.location.fullAddress
          ) {
            this.setFullAddress(this.control.value.location.fullAddress);
          } else {
            this.setLocationCoordinates(this.control.value);
          }
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('control' in changes) {
      this.toggleRemoveLocationButton();
      this.setIconEnd();
    }

    if ('detectLocation' in changes) {
      this.setIconEnd();
    }

    if (
      'countryRestrictions' in changes &&
      this.countryRestrictions &&
      this.autocomplete
    ) {
      this.autocomplete.setComponentRestrictions({
        country:
          this.countryRestrictions.map(country => country.code) || undefined,
      });
    }
  }

  ngOnDestroy(): void {
    if (this.valueResetSubscription) {
      this.valueResetSubscription.unsubscribe();
    }
    if (this.statusChangeSubscription) {
      this.statusChangeSubscription.unsubscribe();
    }
    if (this.placeChangedSubscription) {
      this.placeChangedSubscription.unsubscribe();
    }
    if (this.controlValueChangeSubscription) {
      this.controlValueChangeSubscription.unsubscribe();
    }
  }

  /** Extracts common location information for both Geocoder or Place results  */
  transformResultToLocation(
    place: google.maps.places.PlaceResult | google.maps.GeocoderResult,
  ): Location {
    const addressComponents = place.address_components;
    const countryName = addressComponents
      ? this.getAddressComponentLongName(addressComponents, 'country')
      : undefined;
    const countryCode = addressComponents
      ? this.getAddressComponentShortName(addressComponents, 'country')
      : undefined;
    const administrativeArea = addressComponents
      ? this.getAddressComponentLongName(
          addressComponents,
          'administrative_area_level_1',
        )
      : undefined;
    const administrativeAreaCode = addressComponents
      ? this.getAddressComponentShortName(
          addressComponents,
          'administrative_area_level_1',
        )
      : undefined;

    const locality = addressComponents
      ? this.getAddressComponentLongName(addressComponents, 'locality')
      : undefined;

    const postalTown = addressComponents
      ? this.getAddressComponentLongName(addressComponents, 'postal_town')
      : undefined;

    const neighborhood = addressComponents
      ? this.getAddressComponentLongName(addressComponents, 'neighborhood')
      : undefined;

    /**
     * Initially taken from js/local-jobs/geocoding.js
     * For addresses with no vicinity or locality, we will fallback
     * to postal_town or neightborhood.
     * Example:
     *    - Addresses in United Kingdom have postal town instead of vicinity
     *    - Addresses in Singapore or Hongkong have neighborhood and vicinity is
     *      often the same with country name
     */
    const vicinity =
      ('vicinity' in place &&
        countryName !== place.vicinity &&
        place.vicinity) ||
      (countryName !== locality && locality) ||
      postalTown ||
      neighborhood ||
      administrativeArea ||
      countryName ||
      '';

    const postalCode = addressComponents
      ? this.getAddressComponentShortName(addressComponents, 'postal_code')
      : undefined;

    return {
      administrativeArea,
      administrativeAreaCode,
      fullAddress: place.formatted_address,
      mapCoordinates: place.geometry?.location
        ? {
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng(),
          }
        : undefined,
      vicinity,
      ...(countryName
        ? {
            country: _usersLocationTransformCountry({
              code: countryCode,
              name: countryName,
            }),
          }
        : {}),
      postalCode,
    };
  }

  getAddressComponentLongName(
    addressComponents: google.maps.GeocoderAddressComponent[],
    type: string,
  ): string | undefined {
    const component = addressComponents.find(comp => comp.types.includes(type));
    return component ? component.long_name : undefined;
  }

  getAddressComponentShortName(
    addressComponents: google.maps.GeocoderAddressComponent[],
    type: string,
  ): string | undefined {
    const component = addressComponents.find(comp => comp.types.includes(type));
    return component ? component.short_name : undefined;
  }

  /** Gets full address of location */
  getFullAddress(address: string, postalCode?: string): string {
    if (this.displayPostalCode && postalCode && !address.includes(postalCode)) {
      return `${address}, ${postalCode}`;
    }

    return address;
  }

  /** Sets the native control's value to the fullAddress if provided */
  setFullAddress(fullAddress: string): void {
    this.nativeControl.setValue(fullAddress, {
      emitEvent: false,
    });
  }

  /**
   * Sets the native control's value to the `formatted_address` of the
   * first geocode result from specified coordinates,
   * and updates the input control's value
   * with the transformed geocode result having complete
   * and proper location information
   */
  setLocationCoordinates(coordinates: MapCoordinates): void {
    const location = new google.maps.LatLng(
      coordinates.latitude,
      coordinates.longitude,
    );
    this.googleMaps.geocode({ location }).then(({ results, status }) => {
      // No need to re-enter the Angular zone here, because only the Maps
      // API call runs outside it
      if (status === google.maps.GeocoderStatus.OK && results.length > 0) {
        const locationResult = results[0];

        this.nativeControl.setValue(locationResult.formatted_address);

        this.setLocationControl({
          latitude: coordinates.latitude,
          longitude: coordinates.longitude,
          location: this.transformResultToLocation(locationResult),
          type: locationResult.types.includes('country')
            ? GoogleMapsLocationTypes.COUNTRY
            : GoogleMapsLocationTypes.ADDRESS,
        });

        this.error = undefined;
        this.nativeControl.setErrors(null);
      } else {
        this.control.setValue(undefined);
        this.error = LocationError.DETECT_LOCATION_FAILED;
        this.nativeControl.setErrors({ invalid: '' });
      }
    });
  }

  setLocationControl(location: LocationInputMapLocation): void {
    this.control.setValue(location);
    if (this.displayPostalCode && location.location.fullAddress) {
      this.nativeControl.setValue(location.location.fullAddress, {
        emitEvent: false,
      });
    }
  }

  handleLocationDetect(): void {
    if (this.geolocation.isGeolocationAvailable()) {
      this.geolocation.getCurrentPosition().then(response => {
        if (response.status === 'success') {
          this.setLocationCoordinates(response.position.coords);
        } else {
          this.error =
            response.errorCode === 'PERMISSION_DENIED'
              ? LocationError.DETECT_LOCATION_PERMISSION_DENIED
              : LocationError.DETECT_LOCATION_FAILED;
          this.nativeControl.setErrors({ invalid: '' });
          this.nativeControl.markAsTouched();
        }
      });
    } else {
      this.error = LocationError.DETECT_LOCATION_UNSUPPORTED;
      this.nativeControl.setErrors({ invalid: '' });
    }
  }

  handleLocationRemove(): void {
    this.error = undefined;
    this.nativeControl.reset();
  }

  handleIconClick(): void {
    if (this.showRemoveLocationButton) {
      this.handleLocationRemove();
    } else {
      this.handleLocationDetect();
    }
  }

  toggleRemoveLocationButton(): void {
    this.showRemoveLocationButton = !!(
      this.control.value || this.nativeControl.value
    );
  }

  setIconEnd(): void {
    if (this.showRemoveLocationButton) {
      this.iconEnd = 'ui-close-alt';
      return;
    }

    if (this.detectLocation) {
      this.iconEnd = 'ui-gps';
      return;
    }

    this.iconEnd = undefined;
  }

  /**
   * Fetch place details from location selected in custom autocomplete predictions.
   */
  selectLocation(place: google.maps.places.AutocompletePrediction): void {
    this.googleMaps
      .getLocation(
        place.place_id,
        this.postalCodeRequired,
        this.uiConfig.theme === 'freightlancer'
          ? this.localization.languageCode
          : undefined,
      )
      .then(value => {
        if (value) {
          this.autocompleteSubject$.next([]);
          const location = this.transformResultToLocation(value);
          if (location.mapCoordinates) {
            this.setLocationControl({
              latitude: location.mapCoordinates.latitude,
              longitude: location.mapCoordinates.longitude,
              location: {
                ...location,
                fullAddress: this.getFullAddress(
                  place.description,
                  location.postalCode,
                ),
              },
              type: value.types.includes('country')
                ? GoogleMapsLocationTypes.COUNTRY
                : GoogleMapsLocationTypes.ADDRESS,
            });
          } else {
            // Check if LocationInputCustomAddress
            if (!location.fullAddress) {
              this.error = LocationError.AUTOCOMPLETE_FAILED;
              return;
            }
            this.control.setValue({ fullAddress: location.fullAddress });
          }
          this.validateInput();
        } else {
          this.error = LocationError.AUTOCOMPLETE_FAILED;
          this.nativeControl.setErrors({ invalid: '' });
        }
      });
  }

  handleDynamicValidation(): void {
    if (
      !this.dynamicValidation ||
      this.nativeControl.touched ||
      this.control.valid ||
      this.error !== undefined
    ) {
      this.validateInput();
      return;
    }

    this.nativeControl.markAsPending();
  }

  validateInput(): void {
    const controlValueFullAddress =
      (isLocationInputCustomAddress(this.control.value) &&
        this.control.value.fullAddress) ||
      (isLocationInputMapLocation(this.control.value) &&
        this.control.value.location?.fullAddress);
    if (this.nativeControl.value) {
      if (this.nativeControl.value === controlValueFullAddress) {
        this.error = undefined;
        dirtyAndValidate(this.nativeControl);
      } else {
        /**
         * We set LocationError.SELECT_FROM_DROPDOWN here for two cases:
         * 1) If we are using autocomplete dropdown with debounce, we want to retain
         * current error (if it exists) while waiting to fetch autocomplete predictions.
         * Otherwise, we set LocationError.SELECT_FROM_DROPDOWN.
         * 2) If we are not using autocomplete dropdown with debounce.
         */
        if (this.error === undefined) {
          this.error = LocationError.SELECT_FROM_DROPDOWN;
        }
        this.nativeControl.setErrors({ invalid: '' });
      }
    } else {
      this.error = undefined;
      this.control.setValue(undefined);
      dirtyAndValidate(this.nativeControl);
      this.autocompleteSubject$.next([]);
    }
  }

  trackByPlaceId(
    _: number,
    place: google.maps.places.AutocompletePrediction,
  ): string {
    return place.place_id;
  }
}
